import z from "zod";
import type { InvoiceParty } from "@remi/enums/invoice-party";

const quotingErrorTypeValues = [
  "PRICE_NOT_FOUND",
  "INVALID_QUANTITY",
  "QUANTITY_PROPERTY_MISSING",
  "NEGATIVE_QUANTITY",
  "MISMATCHED_REMOVAL_AND_REINSTALL_QUANTITY",
  "PV_SYSTEM_ID_NOT_FOUND_IN_REMAP",
  "ELECTIVE_DIFF_NON_FINITE_INPUT",
  "ELECTIVE_DIFF_NEGATIVE_QUANTITY",
  "QUOTE_PANEL_VARIANT_MISMATCH",
  "PANEL_LINE_ITEM_PV_SYSTEM_MISMATCH",
  "PANEL_REMOVAL_REINSTALL_QUANTITY_MISMATCH",
  "SALES_DISCOUNT_EXCEEDS_CAP",
] as const;

const quotingErrorTypeSchema = z.enum(quotingErrorTypeValues);
const QuotingErrorType = quotingErrorTypeSchema.enum;
type QuotingErrorType = z.infer<typeof quotingErrorTypeSchema>;

export const PANEL_INVARIANT_ERROR_TYPES = [
  "PANEL_REMOVAL_REINSTALL_QUANTITY_MISMATCH",
  "QUOTE_PANEL_VARIANT_MISMATCH",
  "PANEL_LINE_ITEM_PV_SYSTEM_MISMATCH",
] as const satisfies readonly QuotingErrorType[];
export type PanelInvariantErrorType = (typeof PANEL_INVARIANT_ERROR_TYPES)[number];

export type QuotingErrors =
  | { errorType: typeof QuotingErrorType.PRICE_NOT_FOUND; data: { lineItemTypeName: string; pricingType?: string } }
  | {
      errorType: typeof QuotingErrorType.INVALID_QUANTITY;
      data: { lineItemTypeName: string };
    }
  | { errorType: typeof QuotingErrorType.QUANTITY_PROPERTY_MISSING; data: { lineItemTypeName: string } }
  | { errorType: typeof QuotingErrorType.NEGATIVE_QUANTITY; data: { lineItemTypeName: string; quantity: number } }
  | {
      errorType: typeof QuotingErrorType.MISMATCHED_REMOVAL_AND_REINSTALL_QUANTITY;
      data: { lineItemTypeName: string; pvSystemKey: string; removalQty: number; reinstallQty: number };
    }
  | {
      errorType: typeof QuotingErrorType.PV_SYSTEM_ID_NOT_FOUND_IN_REMAP;
      data: {
        pvSystemId: string;
        candidatePvSystemCount: number;
        reason: "AMBIGUOUS_CANDIDATES" | "STALE_SYSTEM_UNKNOWN" | "STALE_SYSTEM_NOT_EQUIVALENT";
      };
    }
  | {
      errorType: typeof QuotingErrorType.ELECTIVE_DIFF_NON_FINITE_INPUT;
      data: { side: string; field: string; value: number };
    }
  | {
      errorType: typeof QuotingErrorType.ELECTIVE_DIFF_NEGATIVE_QUANTITY;
      data: { side: string; quantity: number };
    }
  | {
      errorType: typeof QuotingErrorType.QUOTE_PANEL_VARIANT_MISMATCH;
      data: { requestForQuoteId: number; r2PanelTypes: string[] };
    }
  | {
      errorType: typeof QuotingErrorType.PANEL_LINE_ITEM_PV_SYSTEM_MISMATCH;
      data: {
        requestForQuoteId: number;
        bidQuoteId?: string;
        side: "removal" | "reinstall";
        expected: number;
        actual: number;
        pvSystemId?: string | null;
        pvSystemName?: string | null;
        variant?: string;
      };
    }
  | {
      errorType: typeof QuotingErrorType.PANEL_REMOVAL_REINSTALL_QUANTITY_MISMATCH;
      data: {
        requestForQuoteId: number;
        variant: "R2" | "R3";
        invoiceParty: InvoiceParty;
        pvSystemId: string | null;
        removalQty: number;
        reinstallQty: number;
      };
    }
  | {
      errorType: typeof QuotingErrorType.SALES_DISCOUNT_EXCEEDS_CAP;
      data: { salesDiscountAmount: number; preDiscountTotal: number; requestForQuoteId: number };
    };

export const quotingErrorDetails: Record<QuotingErrorType, { sentryDescription: string }> = {
  PRICE_NOT_FOUND: {
    sentryDescription: "Price not found for line item type",
  },
  INVALID_QUANTITY: {
    sentryDescription: "Quantity for line item type is not a valid number",
  },
  QUANTITY_PROPERTY_MISSING: {
    sentryDescription: "No quantity property or override function found for line item type",
  },
  NEGATIVE_QUANTITY: {
    sentryDescription: "Quantity for line item type is less than or equal to 0",
  },
  MISMATCHED_REMOVAL_AND_REINSTALL_QUANTITY: {
    sentryDescription: "Removal and reinstall quantity for line item type is not the same",
  },
  PV_SYSTEM_ID_NOT_FOUND_IN_REMAP: {
    sentryDescription: "PV system ID referenced by line item was not found in the remap during quote version creation",
  },
  ELECTIVE_DIFF_NON_FINITE_INPUT: {
    sentryDescription: "Elective diff received a non-finite quantity or unit price (malformed engine output)",
  },
  ELECTIVE_DIFF_NEGATIVE_QUANTITY: {
    sentryDescription: "Elective diff received a negative input quantity (sign belongs on unitPrice)",
  },
  QUOTE_PANEL_VARIANT_MISMATCH: {
    sentryDescription: "Quote classified as R3 has R2 panel line items (no R3 panels)",
  },
  PANEL_LINE_ITEM_PV_SYSTEM_MISMATCH: {
    sentryDescription: "Panel line item quantity does not match PvSystem panel count",
  },
  PANEL_REMOVAL_REINSTALL_QUANTITY_MISMATCH: {
    sentryDescription:
      "R2/R3 removal and reinstall panel quantities are not paired within a pvSystem and invoice party",
  },
  SALES_DISCOUNT_EXCEEDS_CAP: {
    sentryDescription: "Sales discount exceeds the maximum allowed percentage of the pre-discount total",
  },
};
